import type { EquipmentVariant } from './equipmentTypes';

interface EquipmentMiniIconProps {
  variant: EquipmentVariant;
  /** Pixel size — icon is square. Default 28. */
  size?: number;
  /** Tint override; otherwise picks a sensible default per variant. */
  tint?: string;
}

/**
 * Mini SVG icons for the equipment tray buttons. Each one visually
 * matches the EquipmentSvg renderer that draws the same variant on
 * the pitch — so the tray button previews what will land when tapped.
 */
export function EquipmentMiniIcon({ variant, size = 28, tint }: EquipmentMiniIconProps): React.JSX.Element {
  const stroke = 'rgba(0,0,0,0.5)';
  const fill = tint ?? defaultFor(variant);

  // 24×24 viewBox; renderers below stay inside that box.
  const Wrap = (children: React.ReactNode) => (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      {children}
    </svg>
  );

  switch (variant) {
    // Markers --------------------------------------------------------------
    case 'cone':
    case 'cone_training':
      return Wrap(
        <>
          <ellipse cx="12" cy="20" rx="6" ry="1.6" fill="rgba(0,0,0,0.3)" />
          <polygon points="6,20 18,20 12,5" fill={fill} stroke={stroke} strokeWidth="1" />
          <ellipse cx="12" cy="12" rx="3.5" ry="0.6" fill="rgba(255,255,255,0.45)" />
        </>,
      );
    case 'cone_traffic':
      return Wrap(
        <>
          <rect x="4" y="19" width="16" height="2" fill={stroke} />
          <polygon points="6,19 18,19 12,3" fill={fill} stroke={stroke} strokeWidth="1" />
          <ellipse cx="12" cy="11" rx="3.2" ry="0.6" fill="rgba(255,255,255,0.7)" />
        </>,
      );
    case 'cone_disc':
    case 'flat':
      return Wrap(
        <>
          <ellipse cx="12" cy="14" rx="9" ry="2" fill={fill} stroke={stroke} strokeWidth="1" />
          <ellipse cx="12" cy="12" rx="9" ry="2" fill={fill} stroke={stroke} strokeWidth="1" />
        </>,
      );
    case 'gate':
      return Wrap(
        <>
          <line x1="3" y1="14" x2="21" y2="14" stroke={stroke} strokeDasharray="2 2" />
          <polygon points="2,18 7,18 4.5,8" fill={fill} stroke={stroke} strokeWidth="1" />
          <polygon points="17,18 22,18 19.5,8" fill={fill} stroke={stroke} strokeWidth="1" />
        </>,
      );
    case 'corner_flag':
      return Wrap(
        <>
          <line x1="6" y1="3" x2="6" y2="21" stroke={stroke} strokeWidth="1.5" />
          <polygon points="6,3 18,7 6,11" fill={fill} stroke={stroke} strokeWidth="1" />
        </>,
      );

    // Obstacles ------------------------------------------------------------
    case 'mannequin':
      return Wrap(
        <>
          <ellipse cx="12" cy="21" rx="5" ry="1.2" fill="rgba(0,0,0,0.35)" />
          <circle cx="12" cy="5" r="2.4" fill={fill} stroke={stroke} strokeWidth="1" />
          <rect x="10" y="7" width="4" height="13" rx="1.5" fill={fill} stroke={stroke} strokeWidth="1" />
        </>,
      );
    case 'pole':
      return Wrap(
        <>
          <ellipse cx="12" cy="21" rx="4" ry="1" fill="rgba(0,0,0,0.35)" />
          <rect x="11" y="3" width="2" height="18" rx="0.6" fill={fill} stroke={stroke} strokeWidth="0.8" />
        </>,
      );
    case 'hurdle_mini':
      return Wrap(
        <>
          <rect x="3" y="14" width="18" height="2" rx="0.5" fill={fill} stroke={stroke} strokeWidth="0.8" />
          <line x1="5" y1="16" x2="5" y2="20" stroke={stroke} strokeWidth="1.5" />
          <line x1="19" y1="16" x2="19" y2="20" stroke={stroke} strokeWidth="1.5" />
        </>,
      );
    case 'hurdle_med':
      return Wrap(
        <>
          <rect x="3" y="6" width="18" height="2" rx="0.5" fill={fill} stroke={stroke} strokeWidth="0.8" />
          <line x1="5" y1="8" x2="5" y2="20" stroke={stroke} strokeWidth="1.5" />
          <line x1="19" y1="8" x2="19" y2="20" stroke={stroke} strokeWidth="1.5" />
        </>,
      );
    case 'agility_ladder':
      return Wrap(
        <>
          <rect x="6" y="2" width="12" height="20" rx="1" fill="rgba(255,255,255,0.06)" stroke={fill} strokeWidth="1.3" />
          {[5, 9, 13, 17].map((y) => (
            <line key={y} x1="6" y1={y} x2="18" y2={y} stroke={fill} strokeWidth="1" />
          ))}
        </>,
      );
    case 'bench':
      return Wrap(
        <>
          <rect x="3" y="11" width="18" height="3" rx="0.5" fill={fill} stroke={stroke} strokeWidth="0.8" />
          <line x1="5" y1="14" x2="5" y2="20" stroke={stroke} strokeWidth="1.5" />
          <line x1="19" y1="14" x2="19" y2="20" stroke={stroke} strokeWidth="1.5" />
        </>,
      );
    case 'rebounder':
      return Wrap(
        <>
          <rect x="3" y="4" width="18" height="14" rx="1" fill="rgba(255,255,255,0.05)" stroke={fill} strokeWidth="1.3" />
          {[7, 10, 13, 16].map((y) => (
            <line key={y} x1="5" y1={y} x2="19" y2={y} stroke={fill} strokeWidth="0.7" opacity="0.7" />
          ))}
        </>,
      );

    // Goals ----------------------------------------------------------------
    case 'goal_mini':
      return Wrap(
        <>
          <ellipse cx="12" cy="20" rx="6" ry="1" fill="rgba(0,0,0,0.3)" />
          <rect x="7" y="11" width="10" height="8" rx="0.5" fill="rgba(255,255,255,0.05)" stroke="rgba(255,255,255,0.25)" strokeDasharray="2 2" />
          <line x1="7" y1="11" x2="7" y2="19" stroke={fill} strokeWidth="2" />
          <line x1="17" y1="11" x2="17" y2="19" stroke={fill} strokeWidth="2" />
          <line x1="6.5" y1="11" x2="17.5" y2="11" stroke={fill} strokeWidth="2" />
        </>,
      );
    case 'goal_small':
      return Wrap(
        <>
          <ellipse cx="12" cy="20" rx="7" ry="1" fill="rgba(0,0,0,0.3)" />
          <rect x="5" y="9" width="14" height="10" rx="0.5" fill="rgba(255,255,255,0.06)" stroke="rgba(255,255,255,0.28)" strokeDasharray="2 2" />
          <line x1="5" y1="9" x2="5" y2="19" stroke={fill} strokeWidth="2.2" />
          <line x1="19" y1="9" x2="19" y2="19" stroke={fill} strokeWidth="2.2" />
          <line x1="4.5" y1="9" x2="19.5" y2="9" stroke={fill} strokeWidth="2.2" />
        </>,
      );
    case 'goal_7v7':
      return Wrap(
        <>
          <ellipse cx="12" cy="20" rx="8" ry="1" fill="rgba(0,0,0,0.3)" />
          <rect x="3" y="7" width="18" height="12" fill="rgba(255,255,255,0.06)" stroke="rgba(255,255,255,0.28)" strokeDasharray="2 2" />
          <line x1="3" y1="7" x2="3" y2="19" stroke={fill} strokeWidth="2.4" />
          <line x1="21" y1="7" x2="21" y2="19" stroke={fill} strokeWidth="2.4" />
          <line x1="2.5" y1="7" x2="21.5" y2="7" stroke={fill} strokeWidth="2.4" />
          <text x="12" y="16" textAnchor="middle" fontSize="6" fill={fill} fontWeight="700">7</text>
        </>,
      );
    case 'goal_9v9':
      return Wrap(
        <>
          <ellipse cx="12" cy="20" rx="8.5" ry="1" fill="rgba(0,0,0,0.3)" />
          <rect x="2" y="5" width="20" height="14" fill="rgba(255,255,255,0.06)" stroke="rgba(255,255,255,0.28)" strokeDasharray="2 2" />
          <line x1="2" y1="5" x2="2" y2="19" stroke={fill} strokeWidth="2.6" />
          <line x1="22" y1="5" x2="22" y2="19" stroke={fill} strokeWidth="2.6" />
          <line x1="1.5" y1="5" x2="22.5" y2="5" stroke={fill} strokeWidth="2.6" />
          <text x="12" y="15" textAnchor="middle" fontSize="6" fill={fill} fontWeight="700">9</text>
        </>,
      );
    case 'goal_full':
      return Wrap(
        <>
          <ellipse cx="12" cy="21" rx="10" ry="1.2" fill="rgba(0,0,0,0.3)" />
          <rect x="1" y="4" width="22" height="16" fill="rgba(255,255,255,0.08)" stroke="rgba(255,255,255,0.3)" strokeDasharray="2 2" />
          <line x1="1" y1="4" x2="1" y2="20" stroke={fill} strokeWidth="3" />
          <line x1="23" y1="4" x2="23" y2="20" stroke={fill} strokeWidth="3" />
          <line x1="0.5" y1="4" x2="23.5" y2="4" stroke={fill} strokeWidth="3" />
        </>,
      );
    case 'crossbar':
      return Wrap(
        <>
          <line x1="2" y1="12" x2="22" y2="12" stroke={fill} strokeWidth="3.5" strokeLinecap="round" />
          <line x1="2" y1="12" x2="2" y2="20" stroke={fill} strokeWidth="2" opacity="0.5" />
          <line x1="22" y1="12" x2="22" y2="20" stroke={fill} strokeWidth="2" opacity="0.5" />
        </>,
      );
  }
}

function defaultFor(v: EquipmentVariant): string {
  if (v.startsWith('goal') || v === 'crossbar') return '#FFFFFF';
  if (v === 'mannequin' || v === 'pole') return '#bdc3c7';
  if (v === 'corner_flag') return '#E74C3C';
  if (v === 'agility_ladder' || v === 'rebounder') return '#1ABC9C';
  if (v.startsWith('hurdle')) return '#9B59B6';
  if (v === 'bench') return '#95A5A6';
  return '#F39C12';
}
